import { useState } from 'react';
import { Briefcase, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import ToolPage from '@/components/ToolPage';
import { toolCardClass, toolInputClass, toolLabelClass } from '@/lib/toolStyles';

// UK statutory redundancy caps from 6 April 2025
const GB_WEEKLY_PAY_CAP_2025 = 719; // Great Britain: England, Scotland, Wales
const NI_WEEKLY_PAY_CAP_2025 = 749; // Northern Ireland
const MAX_YEARS_SERVICE = 20;

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

interface RedundancyResult {
  eligible: boolean;
  weeksDue: number;
  weeklyPayUsed: number;
  statutoryPay: number;
  uncappedPay: number | null;
  enhancedWeeksPerYear: number;
  enhancedTotalWeeks: number;
  enhancedAmount: number;
  totalPay: number;
  breakdown: { ageBand: string; years: number; weeksPerYear: number; weeks: number }[];
}

function calculateRedundancy(
  annualSalary: number,
  yearsService: number,
  ageAtLeaving: number,
  enhancedWeeksPerYear: number,
  weeklyCap: number
): RedundancyResult {
  const weeklyPay = annualSalary / 52;
  const cappedWeeklyPay = annualSalary > 0 ? Math.min(weeklyPay, weeklyCap) : 0;
  const enhancedTotalWeeks = Math.max(0, enhancedWeeksPerYear) * Math.max(0, yearsService);
  const enhancedAmount = enhancedTotalWeeks * (annualSalary > 0 ? weeklyPay : 0);

  const defaultResult: RedundancyResult = {
    eligible: false,
    weeksDue: 0,
    weeklyPayUsed: cappedWeeklyPay,
    statutoryPay: 0,
    uncappedPay: null,
    enhancedWeeksPerYear: Math.max(0, enhancedWeeksPerYear),
    enhancedTotalWeeks,
    enhancedAmount,
    totalPay: enhancedAmount,
    breakdown: [],
  };

  if (annualSalary <= 0) return defaultResult;

  if (yearsService < 2) {
    return { ...defaultResult, eligible: false };
  }

  const yearsCounted = Math.min(yearsService, MAX_YEARS_SERVICE);
  let yearsUnder22 = 0;
  let years22To40 = 0;
  let years41Plus = 0;

  for (let y = 0; y < yearsCounted; y++) {
    const ageInYear = ageAtLeaving - (yearsCounted - 1 - y);
    if (ageInYear < 22) yearsUnder22++;
    else if (ageInYear <= 40) years22To40++;
    else years41Plus++;
  }

  const weeksUnder22 = yearsUnder22 * 0.5;
  const weeks22To40 = years22To40 * 1;
  const weeks41Plus = years41Plus * 1.5;
  const totalWeeks = weeksUnder22 + weeks22To40 + weeks41Plus;

  const statutoryPay = totalWeeks * cappedWeeklyPay;
  const uncappedPay = weeklyPay > weeklyCap ? totalWeeks * weeklyPay : null;

  const breakdown = [
    ...(yearsUnder22 > 0 ? [{ ageBand: 'Under 22', years: yearsUnder22, weeksPerYear: 0.5, weeks: weeksUnder22 }] : []),
    ...(years22To40 > 0 ? [{ ageBand: '22 to 40', years: years22To40, weeksPerYear: 1, weeks: weeks22To40 }] : []),
    ...(years41Plus > 0 ? [{ ageBand: '41 and over', years: years41Plus, weeksPerYear: 1.5, weeks: weeks41Plus }] : []),
  ];

  return {
    eligible: true,
    weeksDue: totalWeeks,
    weeklyPayUsed: cappedWeeklyPay,
    statutoryPay,
    uncappedPay,
    enhancedWeeksPerYear: Math.max(0, enhancedWeeksPerYear),
    enhancedTotalWeeks,
    enhancedAmount,
    totalPay: statutoryPay + enhancedAmount,
    breakdown,
  };
}

export default function RedundancyCalculator() {
  const [annualSalary, setAnnualSalary] = useState<string>('35000');
  const [yearsService, setYearsService] = useState<string>('5');
  const [ageAtLeaving, setAgeAtLeaving] = useState<string>('45');
  const [enhancedWeeksPerYear, setEnhancedWeeksPerYear] = useState<string>('0');
  const [region, setRegion] = useState<'gb' | 'ni'>('gb');
  const [showInfo, setShowInfo] = useState(false);
  const [showCapInfo, setShowCapInfo] = useState(false);
  const [showSalaryInfo, setShowSalaryInfo] = useState(false);
  const [showYearsInfo, setShowYearsInfo] = useState(false);
  const [showAgeInfo, setShowAgeInfo] = useState(false);
  const [showEnhancedInfo, setShowEnhancedInfo] = useState(false);

  const weeklyCap = region === 'ni' ? NI_WEEKLY_PAY_CAP_2025 : GB_WEEKLY_PAY_CAP_2025;

  const salary = Number(annualSalary) || 0;
  const years = Math.floor(Number(yearsService) || 0);
  const age = Math.floor(Number(ageAtLeaving) || 0);
  const enhancedWeeksPerYearNum = Number(enhancedWeeksPerYear) || 0;

  const result = calculateRedundancy(salary, years, age, enhancedWeeksPerYearNum, weeklyCap);

  const hasError =
    (annualSalary !== '' && (salary < 0 || isNaN(salary))) ||
    (yearsService !== '' && (years < 0 || years > 50)) ||
    (ageAtLeaving !== '' && (age < 16 || age > 100)) ||
    (enhancedWeeksPerYear !== '' && (enhancedWeeksPerYearNum < 0 || isNaN(enhancedWeeksPerYearNum)));

  return (
    <ToolPage
      title="UK Redundancy Calculator"
      description="Estimate statutory and enhanced redundancy pay for your situation in the UK."
      icon={Briefcase}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <Card className={toolCardClass}>
            <CardHeader>
              <h2 className="text-lg font-semibold text-slate-900">Employment Details</h2>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label className={toolLabelClass}>Location in UK</Label>
                </div>
                <select
                  value={region}
                  onChange={(e) => setRegion(e.target.value === 'ni' ? 'ni' : 'gb')}
                  className={`${toolInputClass} rounded-md text-sm`}
                >
                  <option value="gb">Great Britain (England, Scotland, Wales)</option>
                  <option value="ni">Northern Ireland</option>
                </select>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label className={toolLabelClass}>Annual salary</Label>
                  <button type="button" onClick={() => setShowSalaryInfo((p) => !p)} className="text-slate-400 hover:text-slate-700">
                    <HelpCircle className="w-4 h-4" />
                  </button>
                </div>
                {showSalaryInfo && (
                  <p className="text-xs text-slate-500">Gross annual pay before tax. Weekly pay is derived as annual ÷ 52 for the calculation.</p>
                )}
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
                  <Input
                    type="number"
                    min={0}
                    step={1000}
                    value={annualSalary}
                    onChange={(e) => setAnnualSalary(e.target.value)}
                    className={`pl-8 ${toolInputClass}`}
                    placeholder="e.g. 35000"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label className={toolLabelClass}>Years of service</Label>
                  <button type="button" onClick={() => setShowYearsInfo((p) => !p)} className="text-slate-400 hover:text-slate-700">
                    <HelpCircle className="w-4 h-4" />
                  </button>
                </div>
                {showYearsInfo && (
                  <p className="text-xs text-slate-500">Completed full years only. You need at least 2 years to qualify for statutory redundancy.</p>
                )}
                <Input
                  type="number"
                  min={0}
                  max={50}
                  value={yearsService}
                  onChange={(e) => setYearsService(e.target.value)}
                  className={toolInputClass}
                  placeholder="e.g. 5"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label className={toolLabelClass}>Age at leaving</Label>
                  <button type="button" onClick={() => setShowAgeInfo((p) => !p)} className="text-slate-400 hover:text-slate-700">
                    <HelpCircle className="w-4 h-4" />
                  </button>
                </div>
                {showAgeInfo && (
                  <p className="text-xs text-slate-500">Your age when employment ends. Used for statutory age bands: under 22, 22–40, 41+.</p>
                )}
                <Input
                  type="number"
                  min={16}
                  max={100}
                  value={ageAtLeaving}
                  onChange={(e) => setAgeAtLeaving(e.target.value)}
                  className={toolInputClass}
                  placeholder="e.g. 45"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label className={toolLabelClass}>Enhanced (weeks per year)</Label>
                  <button type="button" onClick={() => setShowEnhancedInfo((p) => !p)} className="text-slate-400 hover:text-slate-700">
                    <HelpCircle className="w-4 h-4" />
                  </button>
                </div>
                {showEnhancedInfo && (
                  <p className="text-xs text-slate-500">Extra weeks of base weekly pay per completed year (e.g. 2 = 2 weeks per year). Leave 0 if not applicable.</p>
                )}
                <Input
                  type="number"
                  min={0}
                  step={0.5}
                  value={enhancedWeeksPerYear}
                  onChange={(e) => setEnhancedWeeksPerYear(e.target.value)}
                  className={toolInputClass}
                  placeholder="e.g. 2"
                />
              </div>
              {hasError && (
                <Alert variant="destructive" className="border-red-500/30 bg-red-500/10">
                  <AlertDescription>Please enter valid numbers. Years 0–50, age 16–100, salary and enhanced weeks non-negative.</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className={toolCardClass}>
          <CardHeader className="flex flex-row justify-between items-center">
            <h2 className="text-lg font-semibold text-slate-900">Results</h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowInfo(!showInfo)}
              className="text-slate-500 hover:text-slate-900 hover:bg-slate-100"
            >
              <HelpCircle className="w-5 h-5" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-6">
            {!result.eligible && salary > 0 && (
              <Alert className="border-amber-500/30 bg-amber-500/10">
                <AlertDescription>
                  Statutory redundancy requires at least 2 years of continuous service. With {years} year{years !== 1 ? 's' : ''} service you are not eligible for statutory redundancy pay. Any enhanced severance you enter is still shown below.
                </AlertDescription>
              </Alert>
            )}

            {result.eligible && (
              <>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <p className={`text-sm ${toolLabelClass} mb-1`}>Statutory weeks</p>
                    <p className="text-2xl font-bold text-slate-900">{result.weeksDue.toFixed(1)}</p>
                  </div>
                  <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className={`text-sm ${toolLabelClass}`}>Weekly pay (capped)</p>
                    <button
                      type="button"
                      className="text-slate-400 hover:text-slate-700"
                      onClick={() => setShowCapInfo((prev) => !prev)}
                    >
                      <HelpCircle className="w-4 h-4" />
                    </button>
                  </div>
                    <p className="text-lg font-semibold text-slate-900">{formatCurrency(result.weeklyPayUsed)}</p>
                  {showCapInfo && (
                    <p className="mt-1 text-xs text-slate-500">
                      Statutory redundancy is calculated using your gross weekly pay capped at {formatCurrency(weeklyCap)}.
                      If your actual weekly pay is higher, the calculator also shows an uncapped equivalent for comparison.
                    </p>
                  )}
                  </div>
                  <div>
                    <p className={`text-sm ${toolLabelClass} mb-1`}>Statutory pay</p>
                    <p className="text-2xl font-bold text-brand-700">{formatCurrency(result.statutoryPay)}</p>
                  </div>
                  {result.uncappedPay != null && (
                    <div>
                      <p className={`text-sm ${toolLabelClass} mb-1`}>Uncapped equivalent</p>
                      <p className="text-lg font-semibold text-slate-900">{formatCurrency(result.uncappedPay)}</p>
                      <p className="text-xs text-slate-500">If your pay were not capped; statutory amount is limited by the weekly cap.</p>
                    </div>
                  )}
                </div>

                {result.breakdown.length > 0 && (
                  <div>
                    <p className={`text-sm font-medium ${toolLabelClass} mb-2`}>Age band breakdown</p>
                    <ul className="space-y-1 text-sm text-slate-700">
                      {result.breakdown.map((row) => (
                        <li key={row.ageBand}>
                          {row.ageBand}: {row.years} year{row.years !== 1 ? 's' : ''} × {row.weeksPerYear} week{row.weeksPerYear !== 1 ? 's' : ''} = {row.weeks} week{row.weeks !== 1 ? 's' : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}

            {result.enhancedAmount > 0 && (
              <div>
                <p className={`text-sm ${toolLabelClass} mb-1`}>Enhanced severance</p>
                <p className="text-lg font-semibold text-slate-900">
                  {formatCurrency(result.enhancedAmount)}{' '}
                  <span className="text-xs text-slate-500">
                    ({result.enhancedWeeksPerYear} week{result.enhancedWeeksPerYear !== 1 ? 's' : ''}/year × {years} year{years !== 1 ? 's' : ''} = {result.enhancedTotalWeeks.toFixed(1)} weeks)
                  </span>
                </p>
              </div>
            )}

            <div className="pt-4 border-t border-slate-200">
              <p className={`text-sm ${toolLabelClass} mb-1`}>Total payment</p>
              <p className="text-2xl font-bold text-slate-900">{formatCurrency(result.totalPay)}</p>
            </div>

            {showInfo && (
              <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
                <p className={`text-sm ${toolLabelClass}`}>
                  Statutory redundancy in Great Britain and Northern Ireland uses: 0.5 week per year under 22, 1 week per
                  year aged 22–40, and 1.5 weeks per year aged 41+. Only the first 20 years of service count. Weekly pay
                  is capped at a statutory maximum, which is higher in Northern Ireland than in Great Britain.
                </p>
                <p className={`text-sm ${toolLabelClass}`}>
                  This tool is for guidance only. It does not provide financial, tax, or legal advice and does not account
                  for your full personal circumstances. For personalised advice, speak to a qualified adviser or employment
                  lawyer.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <div className="mt-8 space-y-6">
        <Card className={toolCardClass}>
          <CardHeader>
            <h3 className="text-base font-semibold text-slate-900">Understanding redundancy in the UK</h3>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-700">
            <p>
              Statutory redundancy pay is based on your age, completed years of service, and weekly pay (subject to a
              legal cap). Many employers also offer enhanced packages - for example, extra weeks of pay per year of
              service, payment in lieu of notice, or other benefits.
            </p>
            <p>
              The figures here are estimates only. Your contract, company policy, collective agreements, and negotiations
              can all change what you actually receive.
            </p>
          </CardContent>
        </Card>
        <Card className={toolCardClass}>
          <CardHeader>
            <h3 className="text-base font-semibold text-slate-900">Useful links</h3>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <ul className="list-disc list-inside space-y-1">
              <li>
                <a
                  href="https://www.gov.uk/redundancy-your-rights"
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-700 hover:text-brand-900 underline"
                >
                  Gov.uk - Redundancy: your rights
                </a>
              </li>
              <li>
                <a
                  href="https://www.nidirect.gov.uk/articles/redundancy-pay"
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-700 hover:text-brand-900 underline"
                >
                  nidirect (Northern Ireland) - Redundancy pay
                </a>
              </li>
            </ul>
          </CardContent>
        </Card>
        <Card className={toolCardClass}>
          <CardHeader>
            <h3 className="text-base font-semibold text-slate-900">Important disclaimer</h3>
          </CardHeader>
          <CardContent className="text-sm text-slate-700 space-y-2">
            <p>
              This calculator is for general information only. It is not financial advice, investment advice, tax advice,
              or legal advice. Results are based on simplified assumptions and current statutory limits, which can change.
            </p>
            <p>
              Before making decisions about accepting a package, resigning, or bringing a claim, you should speak to a
              qualified professional such as an independent financial adviser, an employment lawyer, or an accredited
              advice service.
            </p>
          </CardContent>
        </Card>
      </div>
    </ToolPage>
  );
}
